import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { COUNTRIES } from "../../data/countries";

/**
 * Compact country dial-code picker.
 * Shows [flag] [+code] with a chevron; opens a searchable list of
 * flag · name · dial-code rows. Purely controlled by `value` (an ISO code)
 * + `onChange` (receives the full country object).
 */
export default function CountryCodePicker({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selected = COUNTRIES.find((c) => c.iso === value) || COUNTRIES[0];

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    setTimeout(() => searchRef.current?.focus(), 30);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dialCode.includes(q) ||
          c.iso.toLowerCase().includes(q)
      )
    : COUNTRIES;

  return (
    <div ref={rootRef} className="relative" data-testid="country-code-picker">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        data-testid="country-code-trigger"
        className="h-11 shrink-0 px-3 flex items-center gap-1.5 bg-[#0C0D10] border border-[#1F2027] border-r-0 text-[#FAFAFA] rounded-none hover:bg-[#12141A] focus:outline-none focus:ring-1 focus:ring-[#00FF66] disabled:opacity-60 disabled:cursor-not-allowed"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-base leading-none" aria-hidden="true">{selected.flag}</span>
        <span
          className="text-sm font-medium"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {selected.dialCode}
        </span>
        <ChevronDown size={13} className="text-[#8A8A93]" />
      </button>

      {open && (
        <div
          data-testid="country-code-menu"
          className="absolute z-50 top-full left-0 mt-1 w-[260px] max-h-[280px] bg-[#0C0D10] border border-[#1F2027] shadow-2xl flex flex-col"
        >
          <div className="p-2 border-b border-[#1F2027] flex items-center gap-2">
            <Search size={13} className="text-[#5A5C66] shrink-0" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code"
              data-testid="country-code-search"
              className="flex-1 bg-transparent border-0 outline-none text-[#FAFAFA] text-xs placeholder:text-[#5A5C66]"
            />
          </div>
          <ul role="listbox" className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-[#5A5C66] text-xs">No matches</li>
            )}
            {filtered.map((c) => {
              const active = c.iso === selected.iso;
              return (
                <li key={c.iso}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-testid={`country-code-option-${c.iso}`}
                    onClick={() => {
                      onChange(c);
                      setOpen(false);
                      setQuery("");
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-[#12141A] ${
                      active ? "bg-[#00FF66]/[0.08] text-[#FAFAFA]" : "text-[#C9C9D1]"
                    }`}
                  >
                    <span className="text-base leading-none shrink-0" aria-hidden="true">{c.flag}</span>
                    <span className="truncate flex-1">{c.name}</span>
                    <span
                      className="text-[#8A8A93] shrink-0"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {c.dialCode}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
